import React from "react";
import Header from "@/components/layout/Header";
import { Trophy, Scale, CalendarRange, Gauge, ShieldQuestion } from "lucide-react";

const FACTORS = [
  "Win / Loss Record", "Strength of Schedule", "Quality Wins", "Tournament Performance",
  "Margin of Victory (capped)", "Recent Performance", "Team Strength", "Roster Quality",
];

export default function HowRankingsWork() {
  return (
    <div className="min-h-screen bg-[#F1F3F6]">
      <Header />
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="flex items-center gap-2 label-overline text-[#E63946]"><Trophy className="w-4 h-4" /> Transparency</div>
          <h1 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-2">How CBR Team Rankings Work</h1>
          <p className="text-white/60 text-sm mt-3">CBR Team Rankings are built on the proprietary <b className="text-white">CBR Team Rating™</b>. The rating drives the ranking — a team's rank is simply the order of Team Ratings across Canada.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8" data-testid="how-rankings-page">
        <Block icon={Scale} title="What influences a rating">
          <p className="text-sm text-slate-600 mb-3">No single factor decides a team's rating. The CBR Team Rating™ weighs many signals together, including:</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {FACTORS.map((f) => <div key={f} className="flex items-center gap-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-sm px-3 py-2"><span className="w-1.5 h-1.5 bg-[#E63946] rounded-full" />{f}</div>)}
          </div>
        </Block>

        <Block icon={CalendarRange} title="Why strength of schedule matters">
          <p className="text-sm text-slate-600">Beating strong opponents means more than beating weak ones. Teams that consistently play — and win against — quality competition earn higher ratings. This rewards teams for challenging themselves rather than padding records against weaker teams.</p>
        </Block>

        <Block icon={Gauge} title="Ranking confidence">
          <p className="text-sm text-slate-600 mb-3">Early-season ratings move more because there's less data. We show a confidence level so you understand how settled a rating is:</p>
          <div className="space-y-2">
            <Conf color="emerald" label="High Confidence" note="~30+ games played" />
            <Conf color="amber" label="Medium Confidence" note="~10–29 games played" />
            <Conf color="slate" label="Low Confidence" note="fewer than ~10 games played" />
          </div>
        </Block>

        <Block icon={CalendarRange} title="Why rankings change">
          <p className="text-sm text-slate-600">Ratings update as new results come in. A quality win can move a team up; a loss to a weaker team can move them down. As the season progresses and more games are played, ratings stabilize and confidence increases.</p>
        </Block>

        <Block icon={ShieldQuestion} title="Why the exact formula is proprietary">
          <p className="text-sm text-slate-600">To keep rankings fair and resistant to manipulation, the exact weighting of each factor is proprietary and evolves over time. We're committed to transparency about <b>what</b> matters and <b>why</b> — but the precise formula remains confidential, like every serious rating system.</p>
        </Block>
      </div>
    </div>
  );
}

function Block({ icon: Icon, title, children }) {
  return <div><div className="flex items-center gap-2 mb-3"><Icon className="w-5 h-5 text-[#E63946]" /><h2 className="font-display text-2xl uppercase tracking-tight">{title}</h2></div>{children}</div>;
}
function Conf({ color, label, note }) {
  const map = { emerald: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", slate: "bg-slate-100 text-slate-600" };
  return <div className="flex items-center gap-3"><span className={`text-xs font-semibold uppercase px-2 py-1 rounded-sm ${map[color]}`}>{label}</span><span className="text-sm text-slate-500">{note}</span></div>;
}
